import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { AiGlyph } from '@main/complykit/components/ai-icon';
import { Button } from '@main/complykit/components/button';
import { IconButton } from '@main/complykit/components/icon-button';
import { Menu, MenuItem } from '@main/complykit/components/menu';
import { Popover } from '@main/complykit/components/popover';
import { Content, Footer } from '@main/complykit/components/slots';
import { Stack } from '@main/complykit/components/stack';
import { Text } from '@main/complykit/components/text';
import { Tooltip } from '@main/complykit/components/tooltip';
import { useStableCallback } from '@main/shared/browser';
import { Promisable } from '@main/shared/types';
import { ReactNode, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorToast } from '../toast';
import { HoverButtonGroupContext } from './hover-button-group';
import { LiveMessage } from './live-message';

type GenerateOption<Modifier extends string | number> = {
  value: Modifier;
  label: string;
};

export interface AskAiButtonProps<T, Modifier extends string | number> {
  /** The chip's label, and its accessible name when it is icon-only. */
  generateLabel: string;
  isDisabled?: boolean;
  /** Why the chip is disabled, shown on hover and focus. */
  disabledTooltip?: string;
  /** The accept action's label; the default is "Accept". */
  acceptLabel?: string;
  /**
   * A custom accept control, in place of the default. It sits inside the
   * recommendation popover, so a ComplyKit Menu opened from it is part of the
   * popover's overlay stack and choosing an item keeps the popover open.
   */
  acceptButton?: (
    onAccept: (recommendation?: T) => void,
    recommendation: T | undefined,
  ) => ReactNode;
  onRecommendationRequest: (modifier?: Modifier) => Promisable<T>;
  onRecommendationAccept: (recommendation: T) => void;
  renderRecommendation?: (recommendation: T) => ReactNode;
  isRecommendationEmpty?: (recommendation: T | undefined) => boolean;
  emptyRecommendationLabel?: ReactNode;
  emptyRecommendationAcceptTooltip?: string;
  /** Variations of the request, offered in a menu from the chip. */
  generateOptions?: GenerateOption<Modifier>[];
  placement?: 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
}

/**
 * The ask-AI chip on an editable field (component-map.md, input actions):
 * the chip twinkles and orbits while the agent answers, and the
 * recommendation opens in a Popover anchored to it.
 */
export function AskAiButton<T, Modifier extends string | number>({
  generateLabel,
  isDisabled = false,
  disabledTooltip,
  acceptLabel,
  acceptButton,
  onRecommendationRequest,
  onRecommendationAccept,
  renderRecommendation,
  isRecommendationEmpty,
  emptyRecommendationLabel,
  emptyRecommendationAcceptTooltip,
  generateOptions,
  placement = 'top-end',
}: AskAiButtonProps<T, Modifier>) {
  const { t } = useTranslation('ui');
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [recommendation, setRecommendation] = useState<T>();
  const [lastModifier, setLastModifier] = useState<Modifier>();
  const [ownMessage, setOwnMessage] = useState('');
  const chipRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const { shortButtons, inCluster, setIsVisible, announce } = useContext(HoverButtonGroupContext);

  const stableIsRecommendationEmpty = useStableCallback(isRecommendationEmpty);
  const hasRecommendation = recommendation !== undefined;
  const isEmptyRecommendation =
    hasRecommendation && (stableIsRecommendationEmpty?.(recommendation) ?? false);

  async function request(modifier?: Modifier) {
    try {
      setLastModifier(modifier);
      setIsLoading(true);
      setRecommendation(undefined);
      setRecommendation(await onRecommendationRequest(modifier));
    } catch {
      errorToast(t('ask-ai-button.requestFailed'));
    } finally {
      setIsLoading(false);
    }
  }

  function onAccept(rec = recommendation) {
    if (rec === undefined || stableIsRecommendationEmpty?.(rec)) {
      return;
    }

    onRecommendationAccept(rec);
    setRecommendation(undefined);
    (announce ?? setOwnMessage)(t('ask-ai-button.acceptedAnnouncement'));
  }

  const isVisible = isLoading || hasRecommendation || isMenuOpen;
  useEffect(() => setIsVisible(id, isVisible), [id, isVisible, setIsVisible]);

  const stableRenderRecommendation = useStableCallback(renderRecommendation);
  const hasRenderer = renderRecommendation !== undefined;
  const recommendationElement = useMemo(() => {
    if (recommendation === undefined) {
      return null;
    }
    if (isEmptyRecommendation) {
      return emptyRecommendationLabel ?? null;
    }
    return hasRenderer ? stableRenderRecommendation(recommendation) : String(recommendation);
  }, [
    emptyRecommendationLabel,
    hasRenderer,
    isEmptyRecommendation,
    recommendation,
    stableRenderRecommendation,
  ]);

  const hasMenu = !!generateOptions?.length;
  const onPress = hasMenu ? undefined : () => void request();
  /* Inside the hover cluster the pill owns the surface and the edge, so the
   * chip is a ghost; on its own (the policy editor toolbar) it carries its edge. */
  const variant = inCluster ? 'ghost' : 'outline';
  const reason = isDisabled ? disabledTooltip : undefined;

  const chip = shortButtons ? (
    <IconButton
      ref={chipRef}
      variant={variant}
      tone="ai"
      size="sm"
      icon={AiGlyph}
      label={generateLabel}
      tooltip={reason}
      isDisabled={isDisabled}
      isPending={isLoading}
      onPress={onPress}
    />
  ) : (
    <Button
      ref={chipRef}
      variant={variant}
      tone="ai"
      size="sm"
      iconStart={AiGlyph}
      isDisabled={isDisabled}
      isPending={isLoading}
      onPress={onPress}
    >
      {generateLabel}
    </Button>
  );
  const seatedChip =
    reason && !shortButtons ? (
      <Tooltip content={reason} placement="top">
        {chip}
      </Tooltip>
    ) : (
      chip
    );

  return (
    <>
      {hasMenu ? (
        <Menu trigger={seatedChip} onOpenChange={setIsMenuOpen}>
          <MenuItem onAction={() => void request()}>{generateLabel}</MenuItem>
          {generateOptions.map((option) => (
            <MenuItem key={option.value} onAction={() => void request(option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      ) : (
        seatedChip
      )}
      <Popover
        anchorRef={chipRef}
        isOpen={hasRecommendation}
        onOpenChange={(isOpen) => {
          if (!isOpen) setRecommendation(undefined);
        }}
        placement={placement}
        size="lg"
        title={t('ask-ai-button.recommendationLabel')}
        closeLabel={t('ask-ai-button.closeLabel')}
      >
        <Content isScrollable maxHeight={480}>
          {/* The recommendation's body size, which a rendered markdown recommendation inherits. */}
          <Text as="div" size="xs" color="muted">
            {recommendationElement}
          </Text>
        </Content>
        <Footer isDivided>
          <Stack direction="row" gap={2} justify="end">
            <Button variant="ghost" onPress={() => setRecommendation(undefined)}>
              {t('ask-ai-button.discardLabel')}
            </Button>
            <Button
              variant="outline"
              iconStart={ArrowPathIcon}
              onPress={() => void request(lastModifier)}
            >
              {t('ask-ai-button.regenerateLabel')}
            </Button>
            {acceptButton?.(onAccept, recommendation) ?? (
              <Tooltip
                content={
                  emptyRecommendationAcceptTooltip ??
                  t('ask-ai-button.emptyRecommendationAcceptTooltip')
                }
                isDisabled={!isEmptyRecommendation}
              >
                <Button tone="ai" isDisabled={isEmptyRecommendation} onPress={() => onAccept()}>
                  {acceptLabel || t('ask-ai-button.acceptLabel')}
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Footer>
      </Popover>
      {announce ? null : <LiveMessage message={ownMessage} onClear={() => setOwnMessage('')} />}
    </>
  );
}
